import React, { createContext, useContext, useState, useEffect } from 'react';
import { auth } from '../services/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    if (!initialized) {
      checkAuth();
    }
  }, [initialized]);

  const checkAuth = async () => {
    try {
      const userData = await auth.getCurrentUser();
      console.log('AuthContext: getCurrentUser response:', {
        hasEmail: !!userData?.email,
        hasUserId: !!userData?.id,
        hasRole: !!userData?.role,
        role: userData?.role,
        fullData: userData
      });
      setUser(userData);
    } catch (error) {
      // Only set user to null if we get a 401 (unauthorized)
      // Other errors might be temporary network issues
      if (error.response?.status === 401) {
        setUser(null);
      }
    } finally {
      setLoading(false);
      setInitialized(true);
    }
  };

  const login = async (credentials) => {
    try {
      const response = await auth.login(credentials);
      console.log('AuthContext: Login response:', response);
      
      // After login, get the full user data to ensure consistency
      const userData = await auth.getCurrentUser();
      console.log('AuthContext: User data after getCurrentUser:', {
        hasEmail: !!userData?.email,
        hasUserId: !!userData?.id,
        hasRole: !!userData?.role,
        role: userData?.role,
        fullData: userData
      });
      
      setUser(userData);
      setError(null);
      return userData;
    } catch (error) {
      console.error('AuthContext: Login error:', error);
      setError(error.response?.data?.message || 'Login failed');
      setUser(null);
      throw error;
    }
  };

  const signup = async (userData) => {
    try {
      setError(null);
      const response = await auth.signup(userData);
      setUser(response);
      // No need to call checkAuth() since we already have the user data
      return response;
    } catch (error) {
      setError(error.response?.data?.message || 'Signup failed');
      throw error;
    }
  };

  const logout = async (onLogoutComplete) => {
    console.log('AuthContext: Starting logout process...');
    try {
      // First call the API to clear the cookie
      await auth.logout();
      console.log('AuthContext: API logout successful');
      
      // Then clear the user state
      setUser(null);
      setError(null);
      
      // Finally call the completion callback
      if (onLogoutComplete) {
        onLogoutComplete();
      }
      console.log('AuthContext: Logout process completed successfully');
    } catch (error) {
      console.error('AuthContext: Logout error:', error);
      // Even if the API call fails, we should still clear the local state
      setUser(null);
      setError(null);
      
      // Call the callback even if logout API fails
      if (onLogoutComplete) {
        onLogoutComplete();
      }
      console.log('AuthContext: Logout process completed with errors');
    }
  };

  const isSuperAdmin = user?.role === 'superadmin';
  const isAdmin = user?.role === 'admin' || isSuperAdmin;

  const value = {
    user,
    loading,
    error,
    isAuthenticated: !!user,
    isAdmin,
    isSuperAdmin,
    login,
    signup,
    logout,
  };

  console.log('AuthContext: Current state:', { user, isAdmin, isSuperAdmin });

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 